import React, { useEffect, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

import StatusCell from '../../../components/Badge/StatusCell';
import BreadCrumbs from '../../../components/BreadCrumbs/BreadCrumbs';
import AssignDriverIcon from '../../../components/Icons/AssignDriverIcon';
import DelistVehicleIcon from '../../../components/Icons/DelistVehicleIcon';
import MakeActiveIcon from '../../../components/Icons/MakeActiveIcon';
import UnAssignDriverIcon from '../../../components/Icons/UnAssignDriverIcon';
import PageHeader from '../../../components/Layout/PageHeader';
import PageLayout from '../../../components/Layout/PageLayout';
import { Modal } from '../../../components/Modal/Modal';
import AssignVehicleModal from '../../../components/Modal/ModalActions/AssignVehicleModal/AssignVehicleModal';
import ListVehicleModal from '../../../components/Modal/ModalActions/ListVehicleModal/ListVehicleModal';
import VehicleTypeNotInFleetModal from '../../../components/Modal/ModalActions/VehicleTypeNotInFleetModal/VehicleTypeNotInFleetModal';
import Page from '../../../components/Page/Page';
import Table from '../../../components/Table/Table';
import ActionRow from '../../../components/Table/TableAccessories/ActionRow';
import ActionRowWithModal from '../../../components/Table/TableAccessories/ActionRowWithModal';
import VehicleListCell from '../../../components/Table/TableMobileRowCell/VehicleListCell';
import useDelistVehicle from '../../../hooks/dashboard/driver-list/useDelistVehicle';
import useMakeActive from '../../../hooks/dashboard/driver-list/useMakeActive';
import useUnAssignDriver from '../../../hooks/dashboard/driver-list/useUnAssignDriver';
import { useAppSelector } from '../../../redux/store';
import { DRIVER_LIST, VEHICLE_LIST } from '../../../routes/routeConstants';

const VehicleList = () => {
  const [pathArr, setPathArr] = useState<Record<string, string>[]>([]);
  const { unAssignDriver, loading: unAssignDriverLoading } =
    useUnAssignDriver();
  const { delistVehicle, loading: delistVehicleLoading } = useDelistVehicle();
  const { makeActive, loading: makeActiveLoading } = useMakeActive();
  const [showModal, setShowModal] = useState(false);
  const [refreshData, setRefreshData] = useState(0);
  const { businessInfo } = useAppSelector((state) => state.businessInfoData);
  // Vehicle awaiting "Make Active" while the fleet-type warning modal is open
  const [vehicleToActivate, setVehicleToActivate] = useState<any>(null);

  const isVehicleTypeInFleet = (type?: string) => {
    const fleetCategories = businessInfo?.fleetCategories;
    // Skip the check when business info hasn't loaded rather than block the action
    if (!fleetCategories || !type) return true;
    return fleetCategories.some(
      (category) => category.trim().toLowerCase() === type.trim().toLowerCase(),
    );
  };

  const handleContinueMakeActive = async () => {
    const vehicle = vehicleToActivate;
    setVehicleToActivate(null);
    await makeActive(vehicle);
    setRefreshData((prev) => prev + 1);
  };

  useEffect(() => {
    setPathArr(() => [
      {
        name: `Driver List`,
        link: `/${DRIVER_LIST}`,
      },
      {
        name: `Vehicle List`,
        link: `/${VEHICLE_LIST}`,
      },
    ]);
    // eslint-disable-next-line
  }, []);

  return (
    <Page title='Logirate | Vehicle List'>
      <PageLayout>
        <BreadCrumbs pathData={pathArr as any} />
        <PageHeader
          title='Vehicle List'
          className='borderless no-bottom-padding'
          customComp={
            <ListVehicles onClick={() => setShowModal(true)}>
              List Vehicles
            </ListVehicles>
          }
        />
        <Table
          key={refreshData}
          border={false}
          tableFilterBoxFeatures={{}}
          tableHeader={{
            'Plate Number': 'fleetPlateNumber',
            'Vehicle Type': 'type',
            'Assigned Driver': 'assignedDriver',
            Status: 'status',
          }}
          tableDataSourceUrl='api/v1/fleets'
          cellsCustomComp={{
            status: <StatusCell statusKey='status' />,
          }}
          moreOptionsComponents={[
            <ActionRow
              key='Delist-Vehicle'
              label='Delist Vehicle'
              width='257px'
              icon={
                delistVehicleLoading ? (
                  <LoadingOutlined />
                ) : (
                  <DelistVehicleIcon />
                )
              }
              actionFn={async (data) => {
                await delistVehicle(data);
              }}
              conditionalRenderFn={(data) => data?.status !== 'Active'}
              freshTable={true}
            />,
            <ActionRowWithModal
              key='Assign-Vehicle-Driver'
              label='Assign Driver'
              width='257px'
              icon={<AssignDriverIcon />}
              modalCompFn={(data) => <AssignVehicleModal data={data} />}
              conditionalRenderFn={(data) =>
                !!data?.assignedDriver || data?.status !== 'Active'
              }
            />,
            <ActionRow
              key='Make-Active'
              label='Make Active'
              width='257px'
              icon={
                makeActiveLoading ? <LoadingOutlined /> : <MakeActiveIcon />
              }
              actionFn={async (data) => {
                if (!isVehicleTypeInFleet(data?.type)) {
                  setVehicleToActivate(data);
                  return;
                }
                await makeActive(data);
              }}
              conditionalRenderFn={(data) => data?.status === 'Active'}
              freshTable={true}
            />,
            <ActionRow
              key='Unassign-Driver'
              label='Unassign Driver'
              width='257px'
              icon={
                unAssignDriverLoading ? (
                  <LoadingOutlined />
                ) : (
                  <UnAssignDriverIcon />
                )
              }
              actionFn={async (data) => {
                await unAssignDriver(data);
              }}
              conditionalRenderFn={(data) => !data?.assignedDriver}
              freshTable={true}
            />,
          ]}
          mobileCellComp={<VehicleListCell />}
          // showPaginationControl={false}
        />
        <Modal showModal={showModal} setShowModal={setShowModal}>
          <ListVehicleModal setRefreshData={setRefreshData} />
        </Modal>
        <Modal
          showModal={!!vehicleToActivate}
          setShowModal={(show: boolean) => !show && setVehicleToActivate(null)}
        >
          <VehicleTypeNotInFleetModal onContinue={handleContinueMakeActive} />
        </Modal>
      </PageLayout>
    </Page>
  );
};

export default VehicleList;

const ListVehicles = styled.div`
  border: 1px solid red;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #5b91f5;
  border: 1px solid #5b91f5;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
`;
